<template>
  <div class="final-step container">
    <div class="row">
      <div class="col-xl-6">
        <div class="section-header final-step__header">
          <h1 class="section-header__title">
            Perfect! Your <span class="decor">Resume</span> is finished
          </h1>

          <p class="section-header__text">
            Congratulations! You are done with your resume. Double-check all the
            information you have provided before downloading. If you want to
            make any changes, you may go back to the editor.
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-5 col-xl-3 order-md-2">
        <button
          v-if="hideDoctype"
          type="button"
          class="download-btn"
          @click.prevent="downloadResume"
        >
          <span class="download-btn__icon">
            <AppIcon componentName="CloudIcon" />
          </span>

          <span class="download-btn__title">Download Resume</span>
        </button>

        <DownloadButton
          v-if="!hideDoctype"
          :loading="isDownloadingResume"
          @download="downloadResume"
        />

        <FinalControls
          class="final-controls"
          @dashboard="goToDashboard"
          @print="printResume"
          @mail="openModal"
          @edit="editResume"
        />
      </div>

      <div class="col-md col-xl-9 order-md-1">
        <div class="viewer-box">
          <PdfViewer :src="previewPDF" v-if="showPDF"> </PdfViewer>
          <transition name="fade">
            <div
              v-if="previewPDF == '' || previewPDF == null"
              class="preview-loader"
            >
              <AppButtonLoader :size="100" color="#4874e6" />
            </div>
          </transition>
        </div>
      </div>
    </div>

    <AppModal :isOpenModal="isOpenModal" width="636px" @close="closeModal">
      <SendViaEmailModal @sendEmail="mailResume" />
    </AppModal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import printJS from "print-js";

import ResumeHash from "@/helpers/resume-hash";
import CompletedTemplate from "@/helpers/completed-template-hash";
import {
  AUTH_NAMES,
  BROWS_TEMPLATE_NAMES,
  SUBSCRIBE_STEPS_NAMES,
  TEMPLATE_EDITING_NAMES,
} from "@/helpers/rout-constants";
import AppToast from "@/helpers/toast-methods";
import UncompletedCreationStep from "@/helpers/uncompleted-creation-step";
import { downloadingMixin } from "@/mixins/DownloadingMixin";
import { showTextMixin } from "@/mixins/ShowTextMixin";

import DownloadButton from "@/components/ui/DownloadButton";
import FinalControls from "@/components/creation/FinalStepControls";
import SendViaEmailModal from "@/components/dashboard/SendViaEmailModal";
import { mixpanelMixin } from "@/mixins/MixPanelMixin";
import CookieHelper from "@/helpers/cookie-helper";
import AppButtonLoader from "@/components/ui/AppEmbedLoader";
import PdfViewer from "@/components/PdfViewer";

export default {
  name: "FinalStepResumeCreation",
  mixins: [downloadingMixin, showTextMixin, mixpanelMixin],

  components: {
    DownloadButton,
    FinalControls,
    SendViaEmailModal,
    PdfViewer,
    AppButtonLoader,
  },

  computed: {
    ...mapGetters({
      isLoggedIn: "auth/isLoggedIn",
      isSubscribed: "user/isSubscribed",
      resume: "resumeCreation/getResume",
      previewAll: "resumeCreation/getResumePreviewAll",
      previewPDF: "resumeCreation/getResumePreviewPDF",
    }),
  },

  data() {
    return {
      isDownloadingResume: false,
      isOpenModal: false,
      loading: false,
      resumeHash: null,
      hideDoctype: false,
      showPDF: false,
    };
  },

  methods: {
    redirectUnsubscribed() {
      if (!this.isSubscribed) {
        this.$router.push({ name: SUBSCRIBE_STEPS_NAMES.SUBSCRIBE_PLANS });
        return false;
      }

      return true;
    },

    redirectUnregisteredIn() {
      if (!this.isLoggedIn) {
        this.$router.push({ name: AUTH_NAMES.REGISTRATION });

        return false;
      }

      return true;
    },
    async downloadResume(format) {
      if (!this.redirectUnregisteredIn()) return;
      if (!this.redirectUnsubscribed()) return;

      this.isDownloadingResume = true;

      this.showTextWillDownload();

      const fileBlob = await this.getFile(this.resumeHash, format);

      const fileName = await this.getFileName(this.resumeHash, format);

      this.initialisationDownloadFile(fileBlob, fileName);

      this.isDownloadingResume = false;
    },

    async printResume() {
      if (!this.redirectUnregisteredIn()) return;
      if (!this.redirectUnsubscribed()) return;

      this.showTextWillPrint();

      const fileBlob = await this.getFile(this.resumeHash);

      printJS(URL.createObjectURL(fileBlob));
    },

    async openModal() {
      if (!this.redirectUnregisteredIn()) return;
      if (!this.redirectUnsubscribed()) return;

      this.isOpenModal = true;
    },

    closeModal() {
      this.isOpenModal = false;
    },

    async mailResume(emailData) {
      if (!this.redirectUnregisteredIn()) return;
      if (!this.redirectUnsubscribed()) return;

      await this.$store.dispatch("loader/pending");
      try {
        const formData = new FormData();
        formData.append("hash", this.resume.hash);
        formData.append("from", emailData.from);
        formData.append("to", emailData.to);
        formData.append("subject", emailData.subject);
        if (emailData.cc_emails) {
          emailData.cc_emails.forEach((email, idx) =>
            formData.append(`cc_emails[${idx}]`, email)
          );
        }
        if (emailData.attachments) {
          emailData.attachments.forEach((file, idx) =>
            formData.append(`attachments[${idx}]`, file)
          );
        }
        formData.append("text", emailData.text);
        await this.$store.dispatch("userDashboard/sendResumeByEmail", formData);

        this.closeModal();
      } catch (err) {
        console.error(err);
      }
      await this.$store.dispatch("loader/done");
    },

    editResume() {
      if (!this.redirectUnregisteredIn()) return;
      this.$router.push({
        name: TEMPLATE_EDITING_NAMES.RESUME_EDITING,
        query: { hash: this.resumeHash },
        params: {
          duplicate: true,
        },
      });
    },
    goToDashboard() {
      if (!this.redirectUnregisteredIn()) return;
      this.$router.push("/dashboard");
    },
  },

  async created() {
    this.resumeHash = this.$route?.query?.uncompletedTemplate;

    if (!this.resumeHash) {
      await this.$router.push({
        name: BROWS_TEMPLATE_NAMES.BROWS_RESUME,
      });
      AppToast.toastError("We can not found your resume...");
      return;
    }

    let flagHideDoctype = CookieHelper.getFlag("hide_doctype", "");

    if (flagHideDoctype == "1") {
      if (this.isSubscribed) {
        this.hideDoctype = false;
      } else {
        this.hideDoctype = true;
      }
    }

    await this.$store.dispatch("loader/pending");

    this.mixpanelTrack("V2-FINALIZERESUME");

    CompletedTemplate.set({ hash: this.resumeHash, type: "resume" });

    this.$store.commit("auth/setAvailableUncompletedResume", false);

    try {
      await this.$store.dispatch("resumeCreation/getResume", this.resumeHash);
      await this.$store.dispatch(
        "resumeCreation/getResumePreviewAll",
        this.resumeHash
      );
    } catch (err) {
      console.error(err);
    }

    if (this.previewPDF != "") {
      this.showPDF = true;
    }

    //prevent right click
    document.addEventListener("contextmenu", (event) => event.preventDefault());

    await this.$store.dispatch("loader/done");
  },

  async beforeMount() {
    await this.$store.dispatch("loader/done");
  },

  async beforeDestroy() {
    await this.$store.dispatch("resumeCreation/cleanResume", this.resumeHash);
  },
};
</script>

<style lang="scss" scoped>
::v-deep .section-header {
  &__title {
    font-size: 32px;

    span {
      color: $primary;
    }
  }

  &__text {
    font-size: 14px;
    color: $light-gray;
    margin-top: 8px;
  }
}

.final-step {
  padding-top: 40px;
  padding-bottom: 94px;

  &__header {
    margin-bottom: 40px;
  }
}

.resume-preview {
  padding-top: 1054/816 * 100%;
  background-color: #fff;
  position: relative;
  width: 100%;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 30px;

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }
}

.final-controls {
  margin: 24px 0;
}

.preview-loader {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(#fff, 0.8);
  backdrop-filter: blur(7px);
}

.download-btn {
  display: flex;
  align-items: center;
  text-align: center;
  width: 100%;
  height: 56px;
  border-radius: 8px;
  background-color: $primary;
  overflow: hidden;
  position: relative;
  padding: 0;

  &__title {
    color: #fff;
    font-weight: 500;
    font-size: 18px;
    line-height: 21px;
    flex-grow: 1;
    margin-left: -10px;
  }

  &__icon {
    height: 100%;
    width: 55px;
    display: inline-flex;
    justify-content: center;
    align-items: center;

    svg {
      fill: #fff;
      width: 24px;
      height: 24px;
    }

    &--arrow {
      svg {
        width: 13px;
        height: 8px;
      }
    }
  }
  &:active {
    background-color: #416fe5;
  }
}

.viewer-box {
  height: 100%;
  width: 100%;
}
</style>
